/**
 * Zoom-like LiveKit control bar.
 * Trainer right tools: Pause for Q&A + Manage topic (aligned like student flow).
 */

import React from "react";
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  MessageSquare,
  MonitorUp,
  Smile,
  PhoneOff,
  ChevronDown,
  Info,
  PauseCircle,
  PlayCircle,
  CircleHelp,
  ChevronUp,
  PictureInPicture2,
} from "lucide-react";

export type LiveKitControlBarProps = {
  inRoom: boolean;
  isMicrophoneEnabled: boolean;
  isCameraEnabled: boolean;
  isScreenShareEnabled: boolean;
  onToggleMicrophone: () => void;
  onToggleCamera: () => void;
  onToggleScreenShare: () => void;
  onLeave: () => void;
  leaveLabel?: "End" | "Leave";
  leaveDisabled?: boolean;
  /** LCM only: show Pause next to End while live. */
  onPauseSession?: () => void;
  pauseSessionDisabled?: boolean;
  onChat?: () => void;
  onReactions?: () => void;
  /** Optional picker anchored above the Reactions control. */
  reactionsPicker?: React.ReactNode;
  /** Document Picture-in-Picture (Chrome/Edge). */
  pipSupported?: boolean;
  pipOpen?: boolean;
  onTogglePip?: () => void;
  /** Trainer-only footer tools */
  showTrainerTools?: boolean;
  sessionDetailsOpen?: boolean;
  onToggleSessionDetails?: () => void;
  qaPaused?: boolean;
  onPauseForQa?: () => void;
  onResumeFromQa?: () => void;
  onManageTopic?: () => void;
  /** When false, show Start topic instead of Manage topic (first topic only). */
  topicStarted?: boolean;
  /**
   * When true with !topicStarted, show Start topic.
   * Later topics auto-start after post-quiz — keep false so Start is hidden.
   */
  canStartTopic?: boolean;
  onStartTopic?: () => void;
  trainerToolsDisabled?: boolean;
  /** All topics finished — hide Start topic / Pause for Q&A (Resume still shown if paused). */
  allTopicsComplete?: boolean;
  /** Primary trainer only — Pause for Q&A / Resume session. */
  canPauseForQa?: boolean;
  /** Primary trainer only — co-trainers keep Q&A board but not topic controls. */
  canManageTopics?: boolean;
  /** Narrow viewport: icon-only controls, tighter padding. */
  compact?: boolean;
};

const barStyle: React.CSSProperties = {
  position: "relative",
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "center",
  alignItems: "center",
  columnGap: 12,
  rowGap: 8,
  flexShrink: 0,
  padding: "10px 16px",
  borderTop: "1px solid rgba(255,255,255,0.08)",
  background: "#141820",
  minHeight: 64,
  boxSizing: "border-box",
  width: "100%",
};

const pillStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  justifyContent: "center",
  gap: 4,
  padding: "6px 8px",
  borderRadius: 16,
  background: "#1a1d26",
  border: "1px solid rgba(255,255,255,0.06)",
  maxWidth: "100%",
  boxSizing: "border-box",
};

function ControlBtn({
  icon,
  label,
  active,
  mutedStyle,
  onClick,
  disabled,
  showChevron,
  compact,
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  mutedStyle?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  showChevron?: boolean;
  compact?: boolean;
}) {
  let background = "transparent";
  let color = "#94a3b8";
  if (disabled) {
    color = "#64748b";
  } else if (mutedStyle && active === false) {
    background = "rgba(220, 38, 38, 0.2)";
    color = "#f87171";
  } else if (active) {
    background = "rgba(255,255,255,0.12)";
    color = "#ffffff";
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: compact ? 0 : 2,
        minWidth: compact ? 44 : 56,
        padding: compact ? "8px 6px" : "6px 8px",
        border: "none",
        borderRadius: 12,
        background,
        color,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        transition: "background 0.15s ease, color 0.15s ease",
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 2,
          lineHeight: 1,
        }}
      >
        {icon}
        {showChevron ? (
          <ChevronDown size={12} style={{ opacity: 0.65 }} />
        ) : null}
      </span>
      {compact ? null : (
        <span
          style={{
            fontSize: 11,
            fontWeight: 500,
            lineHeight: 1.2,
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </span>
      )}
    </button>
  );
}

function FooterActionButton({
  icon,
  label,
  onClick,
  disabled,
  active,
  compact,
}: {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  active?: boolean;
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: compact ? 0 : 6,
        padding: compact ? "8px 10px" : "8px 12px",
        borderRadius: 10,
        border: active
          ? "1px solid rgba(96,165,250,0.45)"
          : "1px solid rgba(255,255,255,0.1)",
        background: active ? "rgba(59,130,246,0.15)" : "rgba(255,255,255,0.04)",
        color: active ? "#93c5fd" : "#cbd5e1",
        fontSize: 12,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        whiteSpace: "nowrap",
      }}
    >
      {icon}
      {compact ? null : label}
    </button>
  );
}

export function LiveKitControlBar({
  inRoom,
  isMicrophoneEnabled,
  isCameraEnabled,
  isScreenShareEnabled,
  onToggleMicrophone,
  onToggleCamera,
  onToggleScreenShare,
  onLeave,
  leaveLabel = "End",
  leaveDisabled = false,
  onPauseSession,
  pauseSessionDisabled = false,
  onChat,
  onReactions,
  reactionsPicker,
  pipSupported = false,
  pipOpen = false,
  onTogglePip,
  showTrainerTools = false,
  sessionDetailsOpen = false,
  onToggleSessionDetails,
  qaPaused = false,
  onPauseForQa,
  onResumeFromQa,
  onManageTopic,
  topicStarted = false,
  canStartTopic = true,
  onStartTopic,
  trainerToolsDisabled = false,
  allTopicsComplete = false,
  canManageTopics = false,
  canPauseForQa = false,
  compact = false,
}: LiveKitControlBarProps) {
  const leaveText =
    leaveDisabled && leaveLabel === "End" ? "Ending…" : leaveLabel;

  return (
    <footer
      style={{
        ...barStyle,
        padding: compact
          ? "8px 8px calc(8px + env(safe-area-inset-bottom, 0px))"
          : barStyle.padding,
        columnGap: compact ? 8 : 12,
        minHeight: compact ? undefined : barStyle.minHeight,
      }}
    >
      <div style={pillStyle}>
        {showTrainerTools ? (
          <>
            <button
              type="button"
              onClick={onToggleSessionDetails}
              title="Session details"
              aria-label="Session details"
              aria-expanded={sessionDetailsOpen}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: compact ? 0 : 6,
                padding: compact ? "8px 10px" : "8px 12px",
                border: sessionDetailsOpen
                  ? "1px solid rgba(59,130,246,0.5)"
                  : "1px solid transparent",
                borderRadius: 12,
                background: sessionDetailsOpen
                  ? "rgba(37,99,235,0.25)"
                  : "transparent",
                color: sessionDetailsOpen ? "#bfdbfe" : "#94a3b8",
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              <Info size={16} />
              {compact ? null : <span>Session details</span>}
              {compact ? null : sessionDetailsOpen ? (
                <ChevronUp size={14} style={{ opacity: 0.7 }} />
              ) : (
                <ChevronDown size={14} style={{ opacity: 0.5 }} />
              )}
            </button>
            <div
              style={{
                width: 1,
                height: 32,
                margin: "0 4px",
                background: "rgba(255,255,255,0.1)",
                flexShrink: 0,
              }}
            />
          </>
        ) : null}
        <ControlBtn
          icon={
            isMicrophoneEnabled ? <Mic size={18} /> : <MicOff size={18} />
          }
          label={isMicrophoneEnabled ? "Mute" : "Unmute"}
          active={isMicrophoneEnabled}
          mutedStyle
          showChevron={!compact}
          disabled={!inRoom}
          onClick={onToggleMicrophone}
          compact={compact}
        />
        <ControlBtn
          icon={
            isCameraEnabled ? <Video size={18} /> : <VideoOff size={18} />
          }
          label={isCameraEnabled ? "Stop Video" : "Start Video"}
          active={isCameraEnabled}
          mutedStyle
          showChevron={!compact}
          disabled={!inRoom}
          onClick={onToggleCamera}
          compact={compact}
        />
        <ControlBtn
          icon={<MessageSquare size={18} />}
          label="Chat"
          disabled={!inRoom}
          onClick={onChat}
          compact={compact}
        />
        <ControlBtn
          icon={<MonitorUp size={18} />}
          label={isScreenShareEnabled ? "Stop Share" : "Share"}
          active={isScreenShareEnabled}
          disabled={!inRoom}
          onClick={onToggleScreenShare}
          compact={compact}
        />
        {!compact && pipSupported && onTogglePip ? (
          <ControlBtn
            icon={<PictureInPicture2 size={18} />}
            label={pipOpen ? "Exit PiP" : "PiP"}
            active={pipOpen}
            disabled={!inRoom}
            onClick={onTogglePip}
            compact={compact}
          />
        ) : null}
        <div style={{ position: "relative" }}>
          <ControlBtn
            icon={<Smile size={18} />}
            label="Reactions"
            disabled={!inRoom}
            onClick={onReactions}
            compact={compact}
          />
          {reactionsPicker}
        </div>
        {onPauseSession && leaveLabel === "End" ? (
          <button
            type="button"
            onClick={onPauseSession}
            title="Pause"
            aria-label="Pause session"
            disabled={pauseSessionDisabled || leaveDisabled}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: compact ? 0 : 8,
              marginLeft: 4,
              padding: compact ? "10px 12px" : "10px 16px",
              border: "1px solid rgba(251,191,36,0.55)",
              borderRadius: 12,
              background: "rgba(245,158,11,0.18)",
              color: "#fbbf24",
              fontSize: 14,
              fontWeight: 600,
              cursor:
                pauseSessionDisabled || leaveDisabled
                  ? "not-allowed"
                  : "pointer",
              opacity: pauseSessionDisabled || leaveDisabled ? 0.7 : 1,
              flexShrink: 0,
            }}
          >
            <PauseCircle size={16} />
            {compact ? null : "Pause"}
          </button>
        ) : null}
        <button
          type="button"
          onClick={onLeave}
          title={leaveText}
          aria-label={leaveText}
          disabled={leaveDisabled}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: compact ? 0 : 8,
            marginLeft: 4,
            padding: compact ? "10px 12px" : "10px 16px",
            border: "none",
            borderRadius: 12,
            background: leaveDisabled ? "#991b1b" : "#dc2626",
            color: "#ffffff",
            fontSize: 14,
            fontWeight: 600,
            cursor: leaveDisabled ? "not-allowed" : "pointer",
            opacity: leaveDisabled ? 0.7 : 1,
            flexShrink: 0,
          }}
        >
          <PhoneOff size={16} />
          {compact ? null : leaveText}
        </button>
      </div>

      {showTrainerTools && (canPauseForQa || canManageTopics) ? (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            flex: "0 1 auto",
            maxWidth: "100%",
          }}
        >
          {/* After all topics: hide Pause/Start; keep Resume while Q&A is active. */}
          {canPauseForQa && (!allTopicsComplete || qaPaused) && (
            <FooterActionButton
              icon={
                qaPaused ? (
                  <PlayCircle size={14} />
                ) : (
                  <PauseCircle size={14} />
                )
              }
              label={qaPaused ? "Resume session" : "Pause for Q&A"}
              onClick={qaPaused ? onResumeFromQa : onPauseForQa}
              disabled={!qaPaused && trainerToolsDisabled}
              active={qaPaused}
              compact={compact}
            />
          )}
          {canManageTopics &&
            !allTopicsComplete &&
            (topicStarted || canStartTopic) && (
            <FooterActionButton
              icon={<CircleHelp size={14} />}
              label={topicStarted ? "Manage topic" : "Start topic"}
              onClick={topicStarted ? onManageTopic : onStartTopic}
              disabled={
                trainerToolsDisabled ||
                qaPaused ||
                (topicStarted ? !onManageTopic : !onStartTopic)
              }
              compact={compact}
            />
          )}
        </div>
      ) : null}
    </footer>
  );
}

export default LiveKitControlBar;
